import React, { useRef, useState, useEffect } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { EffectComposer, Bloom } from "@react-three/postprocessing";

export default function MetaCubeOracleV1({
  running = true,
  chaos = false,
  glow = true,
  cubeSpeedPct = 50,
  ringSpeedPct = 50,
  colorShiftRatePct = 30,
  bloomIntensity = 1.2,
  bloomRadius = 0.6,
  bloomThreshold = 0.0,
  onEntropyUpdate = () => {},
}) {
  const groupRef = useRef();
  const [hue, setHue] = useState(0);
  const [entropy, setEntropy] = useState(0);

  // --- Conversion curseurs en facteurs visuels ---
  const cubeVisualFactor = (cubeSpeedPct / 100) * 1.2;
  const ringVisualFactor = (ringSpeedPct / 100) * 0.9;

  // --- Entropie dynamique via backend /dyn_entropy ---
  useEffect(() => {
    if (!running) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch("/api/geometry/metacube_oracle/dyn_entropy", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            rotation_speed: cubeSpeedPct / 100,
            turbulence: Math.random() * 1.2,
            resonance: Math.random() * 1.5,
            chaos_factor: chaos ? 1.5 + Math.random() : 0.8,
            glow,
          }),
        });

        const data = await res.json();
        if (typeof data.entropy_dyn === "number") {
          // Lissage fluide
          setEntropy((prev) =>
            prev === 0 ? data.entropy_dyn : prev + (data.entropy_dyn - prev) * 0.3
          );
          onEntropyUpdate(data.entropy_dyn);
        }
      } catch (err) {
        console.error("Erreur fetch dyn_entropy MetaCube:", err);
      }
    }, 2000); // toutes les 2 secondes

    return () => clearInterval(interval);
  }, [running, cubeSpeedPct, chaos, glow, onEntropyUpdate]);

  // --- Animation 3D ---
  useFrame((_, delta) => {
    if (!running || !groupRef.current) return;

    const t = Date.now() * 0.001;

    const cube = groupRef.current.getObjectByName("metaCube");
    if (cube) {
      cube.rotation.x += cubeVisualFactor * delta;
      cube.rotation.y += cubeVisualFactor * delta * 1.3;
      cube.rotation.z += cubeVisualFactor * delta * 0.9;

      const cubeMat = cube.material;
      if (cubeMat) {
        const newHue = (hue + colorShiftRatePct * 0.002) % 1;
        setHue(newHue);
        cubeMat.color.setHSL(newHue, 0.8, 0.5);
        cubeMat.emissive.setHSL(newHue, 0.6, glow ? 0.35 : 0);
      }
    }

    for (let i = 0; i < 3; i++) {
      const ring = groupRef.current.getObjectByName(`ring-${i}`);
      if (ring) {
        ring.rotation.x += (ringVisualFactor * delta) * (i % 2 === 0 ? 1 : -1);
        ring.rotation.y += (ringVisualFactor * delta * 1.5) * (i % 2 === 0 ? -1 : 1);

        const hueShift = (hue + i * 0.15) % 1;
        ring.material.color.setHSL(hueShift, 0.7, 0.5);
        ring.material.emissive.setHSL(hueShift, 0.5, glow ? 0.25 : 0);
      }
    }

    if (chaos) {
      groupRef.current.position.x = Math.sin(t * 2) * 0.6;
      groupRef.current.position.y = Math.cos(t * 3) * 0.5;
    }
  });

  return (
    <>
      <group ref={groupRef}>
        {/* Cube central */}
        <mesh name="metaCube">
          <boxGeometry args={[5, 5, 5]} />
          <meshPhongMaterial
            color={new THREE.Color().setHSL(hue, 0.8, 0.5)}
            emissive={new THREE.Color().setHSL(hue, 0.6, 0.3)}
            shininess={120}
          />
        </mesh>

        {/* Anneaux */}
        {[0, 1, 2].map((i) => (
          <mesh key={i} name={`ring-${i}`} rotation={[Math.PI / 3 * i, Math.PI / 4 * i, 0]}>
            <torusGeometry args={[8 + i * 2, 0.4, 16, 100]} />
            <meshPhongMaterial
              color={new THREE.Color().setHSL((hue + i * 0.15) % 1, 0.7, 0.5)}
              emissive={new THREE.Color().setHSL((hue + i * 0.15) % 1, 0.5, 0.25)}
              shininess={100}
              transparent
              opacity={0.9}
            />
          </mesh>
        ))}
      </group>

      <EffectComposer>
        <Bloom intensity={bloomIntensity} radius={bloomRadius} threshold={bloomThreshold} />
      </EffectComposer>
    </>
  );
}
